import { PathOptions } from "leaflet";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { diasDescanso, diasOcupacion } from "../../../../../utils/ganaderiaUtils";
import { _miles } from "../../../../../utils/numberUtils";
import { BaseMapRender, MapGroup, PopupConfig } from "../../../../base/map/BaseMapRender";
import { OpacidadControl } from "../../../../implementations/map/OpacidadControl";
import { ConfigTopActions } from "../../../../implementations/map/TopActions";
import { GanaderiaBottomSheetRender } from "../../lote/map/GanaderiaBottomSheetRender";

/** Opacidad del relleno de los potreros, que se cambia con el deslizador de la barra superior
 *  (pedido del usuario, 2026-10-07). Se mantiene mientras la app está abierta (al cambiar de finca
 *  o de página); al recargar vuelve a 50 %, el valor fijo de antes. No se guarda en el navegador. */
let opacidadRelleno = 0.5;

/** Mapa para fincas ganaderas. Al seleccionar un potrero muestra su detalle en un
 *  bottom-sheet (componente propio) en vez de popup. Tooltip con datos de animales;
 *  solo expone el toggle de ortomosaico (sin conteo). */
export class GanaderiaMapRender extends BaseMapRender<ClienteFincaLoteResponse> {
  /**
   * @param _onRpv       Acción "Pesar / Rotar / Veterinaria" del bottom-sheet,
   *                     con el potrero seleccionado (el botón solo se muestra si
   *                     se provee).
   * @param _onHistorico Acción "Histórico" del bottom-sheet, con el potrero
   *                     seleccionado (el botón solo se muestra si se provee).
   */
  constructor(
    private readonly _onRpv?: (lote: ClienteFincaLoteResponse) => void,
    private readonly _onHistorico?: (lote: ClienteFincaLoteResponse) => void,
  ) {
    super();
  }

  /** Sin datos extra: el lote ya trae los campos de ganadería. */
  protected doInitData(): Promise<ClienteFincaLoteResponse[]> {
    return Promise.resolve([]);
  }

  /** El "response" del lote es el propio lote. */
  protected doGetResponse(
    lote: ClienteFincaLoteResponse,
    _responseList: ClienteFincaLoteResponse[],
  ): ClienteFincaLoteResponse | null {
    return lote;
  }

  /** Libera el bottom-sheet si quedó montado y el control de opacidad. */
  protected doDispose(): void {
    this._sheet?.dispose();
    this._opacidadControl?.dispose();
    this._opacidadControl = null;
  }

  /** Estilo del potrero, coloreado con el mismo semáforo en ambos estados
   *  (rojo = requiere acción ya):
   *  - **Ocupado** (animales > 0): días de ocupación vs. óptimo de ocupación.
   *  - **En descanso** (animales = 0): días de descanso vs. óptimo de descanso.
   *  Sin dato de días o sin óptimo configurado: estilo base (sin relleno). */
  protected getStyle(
    lote: ClienteFincaLoteResponse,
    response: ClienteFincaLoteResponse | null,
  ): PathOptions {
    const style = super.getStyle(lote, response);

    const fillColor =
      lote.ganaderiaCantidadAnimales > 0
        ? this._colorPorRatio(
            diasOcupacion(lote),
            this._finca.ganaderiaDiasOcupacionOptimo,
          )
        : this._colorPorRatio(
            diasDescanso(lote),
            this._finca.ganaderiaDiasDescansoOptimo,
          );

    if (!fillColor) return style;
    return { ...style, fillColor, fillOpacity: opacidadRelleno };
  }

  /** Semáforo de días vs. óptimo: verde (<80%), amarillo (≥80%), rojo
   *  (≥ óptimo); null si no hay días u óptimo configurado. */
  private _colorPorRatio(
    dias: number | null,
    optimo: number,
  ): string | null {
    if (dias == null || optimo <= 0) return null;
    const ratio = dias / optimo;
    return ratio < 0.8 ? "#16a34a" : ratio < 1 ? "#d97706" : "#dc2626";
  }

  /** Tooltip: default + Animales / Días descanso / Días ocupación; ortomosaico
   *  sí, conteo no. */
  protected getConfigTopActions(): ConfigTopActions<ClienteFincaLoteResponse> {
    const toolTipItems = this.getDefaultTooltipItems();
    toolTipItems.push(
      {
        get(_response, lote) {
          return _miles(lote.ganaderiaCantidadAnimales);
        },
        key: "animales",
        label: "Animales",
      },
      {
        get(_response, lote) {
          const dias = diasDescanso(lote);
          return dias != null ? `${dias} días` : "—";
        },
        key: "diasDescanso",
        label: "Días descanso",
      },
      {
        get(_response, lote) {
          const dias = diasOcupacion(lote);
          return dias != null ? `${dias} días` : "—";
        },
        key: "diasOcupacion",
        label: "Días ocupación",
      },
    );
    return {
      toolTipItems,
      withOrtomosaico: true,
      withConteo: false,
      onMountEnd: (element, _map, potreros) => this._montarOpacidad(element, potreros),
    };
  }

  /** Control de opacidad de la barra superior (o null si no está montado). */
  private _opacidadControl: OpacidadControl | null = null;

  /** Monta el deslizador de opacidad: al moverlo cambia el relleno de todos los potreros al
   *  instante, sin perder su color ni la selección (el borde amarillo se mantiene). Los
   *  potreros sin color (solo borde) no se notan. */
  private _montarOpacidad(
    element: HTMLElement,
    potreros: MapGroup<ClienteFincaLoteResponse>[],
  ): void {
    this._opacidadControl?.dispose();
    this._opacidadControl = new OpacidadControl();
    this._opacidadControl.init(element, opacidadRelleno, (valor) => {
      opacidadRelleno = valor;
      potreros.forEach((potrero) => potrero.layer.setStyle({ fillOpacity: valor }));
    });
  }

  /** Ganadería usa bottom-sheet, no popup. */
  protected getPopupConfig(): PopupConfig<ClienteFincaLoteResponse> | null {
    return null;
  }

  /** Componente del sheet actualmente montado (o null). */
  private _sheet: GanaderiaBottomSheetRender | null = null;

  /** Al seleccionar un potrero: monta el bottom-sheet con su detalle. */
  protected onResponseSelected(
    lote: ClienteFincaLoteResponse,
    _response: ClienteFincaLoteResponse | null,
    bottomSheet: HTMLDivElement,
  ): void {
    this._sheet?.dispose();
    this._sheet = new GanaderiaBottomSheetRender(
      lote,
      this._finca,
      () => this.openLoteEdit(lote),
      this._onRpv ? () => this._onRpv!(lote) : undefined,
      this._onHistorico ? () => this._onHistorico!(lote) : undefined,
    );
    bottomSheet.innerHTML = this._sheet.render();
    this._sheet.connect();
    bottomSheet.classList.add("open");
    // El sheet le quita altura al mapa (flex): recalcular el tamaño de Leaflet.
    requestAnimationFrame(() => this.invalidateMapSize());
  }

  /** Al deseleccionar: desmonta y cierra el sheet. */
  protected onLoteRemove(bottomSheet: HTMLDivElement): void {
    this._sheet?.dispose();
    this._sheet = null;
    bottomSheet.classList.remove("open");
    bottomSheet.innerHTML = "";
    // El mapa recupera la altura del sheet: recalcular el tamaño de Leaflet.
    requestAnimationFrame(() => this.invalidateMapSize());
  }
}
